
import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { settingsService } from "../services/settingsService";
import {
    Terminal as TerminalIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Fullscreen as MaximizeIcon,
    FullscreenExit as MinimizeIcon,
    ErrorOutline as AlertCircleIcon,
    Description as FileTextIcon
} from "@mui/icons-material";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";

// Custom dark terminal theme
const terminalTheme = {
    background: '#1e1e1e', // VS Code dark like
    foreground: '#d4d4d4',
    green: '#4caf50',
    red: '#f44336',
    yellow: '#ff9800',
    blue: '#2196f3',
    header: '#2d2d2d',
    selection: '#264f78'
};

const SystemLogs = () => {
    const [logs, setLogs] = useState("");
    const [loading, setLoading] = useState(false);
    const [logType, setLogType] = useState("access");
    const [lines, setLines] = useState(100);
    const [isMaximized, setIsMaximized] = useState(false);
    const logContainerRef = useRef(null);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getSystemLogs(logType, lines);

            if (response.status) {
                setLogs(response.data);
            } else {
                toast.error(response.message || "Failed to fetch logs");
                setLogs(`[ERROR] ${response.message || "Failed to fetch logs"} `);
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching logs");
            setLogs(`[CRITICAL] Error fetching logs: ${error.message || error} `);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [logType, lines]);

    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [logs]);

    const handleLogTypeChange = (event, newLogType) => {
        if (newLogType !== null) {
            setLogType(newLogType);
        }
    };

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 100px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TerminalIcon sx={{ fontSize: 32 }} />
                        System Logs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Monitor server activity and errors in real-time
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={logType}
                        exclusive
                        onChange={handleLogTypeChange}
                        aria-label="log type"
                        size="small"
                        sx={{ bgcolor: 'background.paper' }}
                    >
                        <ToggleButton value="access" aria-label="access logs">
                            <FileTextIcon sx={{ fontSize: 16, mr: 1 }} />
                            Access
                        </ToggleButton>
                        <ToggleButton value="error" aria-label="error logs" color="error">
                            <AlertCircleIcon sx={{ fontSize: 16, mr: 1 }} />
                            Error
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Select
                        value={lines}
                        onChange={(e) => setLines(e.target.value)}
                        size="small"
                        sx={{ bgcolor: 'background.paper', minWidth: 120 }}
                    >
                        <MenuItem value={50}>Last 50 lines</MenuItem>
                        <MenuItem value={100}>Last 100 lines</MenuItem>
                        <MenuItem value={500}>Last 500 lines</MenuItem>
                        <MenuItem value={1000}>Last 1000 lines</MenuItem>
                    </Select>

                    <Tooltip title="Refresh Logs">
                        <IconButton
                            onClick={fetchLogs}
                            disabled={loading}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 20 }} className={loading ? 'animate-spin' : ''} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Paper
                elevation={6}
                sx={{
                    bgcolor: terminalTheme.background,
                    color: terminalTheme.foreground,
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: isMaximized ? 'calc(100vh - 40px)' : 'calc(100% - 80px)',
                    position: isMaximized ? 'fixed' : 'relative',
                    top: isMaximized ? 20 : 'auto',
                    left: isMaximized ? 20 : 'auto',
                    right: isMaximized ? 20 : 'auto',
                    zIndex: isMaximized ? 1300 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #333'
                }}
            >
                {/* Terminal Header */}
                <Box sx={{
                    bgcolor: terminalTheme.header,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #333'
                }}>
                    <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: terminalTheme.red }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: terminalTheme.yellow }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: terminalTheme.green }} />
                    </Box>
                    <Typography sx={{
                        fontFamily: 'Monaco, monospace',
                        fontSize: '0.8rem',
                        color: '#888',
                        flex: 1,
                        textAlign: 'center'
                    }}>
                        admin@utiliti-server:~/{logType}.log
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setIsMaximized(!isMaximized)}
                        sx={{ color: '#888', '&:hover': { color: 'white' } }}
                    >
                        {isMaximized ? <MinimizeIcon sx={{ fontSize: 16 }} /> : <MaximizeIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Box>

                {/* Terminal Content */}
                <Box
                    ref={logContainerRef}
                    sx={{
                        p: 2,
                        flex: 1,
                        overflow: 'auto',
                        fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        '&::-webkit-scrollbar': {
                            width: '10px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#1e1e1e',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#424242',
                            borderRadius: '5px',
                            border: '2px solid #1e1e1e',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#555',
                        }
                    }}
                >
                    {loading && !logs ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: terminalTheme.yellow }}>
                            <RefreshIcon sx={{ fontSize: 16 }} className="animate-spin" />
                            <Typography component="span" sx={{ fontFamily: 'inherit' }}>
                                Establishing secure connection to server logs...
                            </Typography>
                        </Box>
                    ) : (
                        logs ? (
                            logs.split('\n').map((line, index) => {
                                if (!line) return null;
                                // Simple syntax highlighting logic
                                let color = terminalTheme.foreground;
                                if (line.toLowerCase().includes('error')) color = terminalTheme.red;
                                else if (line.toLowerCase().includes('warn')) color = terminalTheme.yellow;
                                else if (line.toLowerCase().includes('success') || line.includes('200')) color = terminalTheme.green;
                                else if (line.includes('GET') || line.includes('POST')) color = terminalTheme.blue;

                                return (
                                    <div key={index} style={{ color, display: 'flex' }}>
                                        <span style={{
                                            color: '#555',
                                            marginRight: '16px',
                                            userSelect: 'none',
                                            minWidth: '40px',
                                            textAlign: 'right'
                                        }}>
                                            {index + 1}
                                        </span>
                                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{line}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <Typography sx={{ color: '#666', fontFamily: 'inherit', fontStyle: 'italic' }}>
                                -- No logs to display --
                            </Typography>
                        )
                    )}
                    {/* Blink cursor effect at the end */}
                    {!loading && <span className="animate-pulse" style={{ color: terminalTheme.green, marginLeft: '58px' }}>_</span>}
                </Box>
            </Paper>
            <ToastContainer />
        </Box>
    );
};

export default SystemLogs;
